/**
 * Shared API route handler functions
 * These contain the business logic extracted from API routes
 * Routes become thin wrappers that call these functions
 */

import { getGoogleTokensFromSupabase, getMsftTokensFromSupabase } from './supabase';
import { CalendarService, DEFAULT_CONFIG } from './calendar-service';
import {
  getGoogleUserEmail,
  getMicrosoftUserEmail,
  type AccountStatus,
  createErrorResponse,
  createSuccessResponse,
} from './api-shared-utils';
import { getEventTypeConfig } from './booking/event-types-config';
import { getNextAssignee } from './booking/round-robin-assignment';

// Import handler utilities
import {
  getTokensForEmail,
  getGoogleTokens,
  getMicrosoftTokens,
  validateTokenExists,
  checkEmailsTokenStatus,
  createCalendarNotConnectedError,
} from './handlers/token-management';
import {
  hasProfessionalEmailCalendarConfig,
  fetchProfessionalEmailCalendarWithResolution,
  createProfessionalEmailCalendarEventViaHub,
} from './professional-email-calendar-bridge';
import {
  validateOpenSlotsParams,
  validatePublicBookingParams,
  validatePublicAvailabilityParams,
  validateConnectionType,
} from './handlers/validation';
import {
  logHandlerStart,
  logHandlerError,
  logOpenSlotsCalculation,
  logPublicBookingCreated,
  logAccountDataCleared,
  logConnectionRemoved,
  logConnectionRemovalFailed,
  logExternalEventCreated,
  logPartialAvailability,
  logSlotFiltering,
  logRoundRobinAssignment,
  logTimezoneConversion,
  logBookingHistoryStored,
  logBookingHistoryError,
} from './handlers/logging';
import {
  buildEventDetails,
  createGoogleCalendarEvent,
  createMicrosoftCalendarEventForBooking,
  extractMeetingDetails,
  extractMicrosoftMeetingDetails,
} from './handlers/calendar-event-builder';
import {
  deduplicateSlots,
  filterSlotsByDuration,
  processAvailableSlots,
} from './handlers/slot-processing';
import {
  removeConnectionByType,
  removeAllConnections,
} from './handlers/connection-handlers';
import { supabaseAdmin } from './supabase-auth';
import { getEmailClientAddressFromRole } from './email-client-address';

// ============================================================================
// TYPES
// ============================================================================

export interface RouteContext {
  userEmail: string;
  request: Request;
  clientIP?: string;
  userAgent?: string;
}

export interface OpenSlotsParams {
  emails: string[];
  startDate: string;
  endDate: string;
  slotDuration?: number;
  extendedHours?: boolean;
}

export interface PublicBookingParams {
  userEmail: string;
  email: string;
  description?: string;
  location?: string;
  isOnline?: boolean;
  bookeeEmail?: string; // Optional if eventType is provided
  eventType?: string; // Event type for round robin assignment
  slot: {
    start: string;
    end: string;
  };
}

async function resolveEmailClientAddressFromAuthEmail(authEmail: string): Promise<string | null> {
  if (!supabaseAdmin) return null;

  const { data: appUser, error: appUserError } = await supabaseAdmin
    .from("app_users")
    .select(
      `
        id,
        email,
        first_name,
        last_name,
        role_id,
        roles:role_id (
          name
        )
      `
    )
    .eq("email", authEmail)
    .maybeSingle();
  if (appUserError || !appUser) return null;

  const roleName = (appUser.roles as any)?.name as string | undefined;
  const derivedEmail = getEmailClientAddressFromRole({
    role: roleName,
    firstName: appUser.first_name,
    lastName: appUser.last_name,
  });

  return derivedEmail || null;
}

// Canonical Hub CRM origin (main-04: hub.westtn.consulting, never a legacy TLD).
const HUB_BASE_URL = 'https://hub.westtn.consulting';

/**
 * Build the internal "Team Notes" footer appended to a public-booking calendar
 * invite. Carries the meeting type and a Hub CRM deep link so the WTC attendee
 * can open the customer's record in one click. The customer is also an attendee
 * and will see this block — it is intentionally harmless to them (a link they
 * cannot open) and useful to the rep.
 *
 * Best-effort: resolving the booker to a Hub contact is a convenience. A missing
 * contact (brand-new lead) or a lookup failure must NEVER block the booking — we
 * fall back to a CRM search link.
 */
async function buildInternalInviteFooter(params: {
  bookerEmail: string;
  eventType?: string;
  eventName?: string;
}): Promise<string> {
  const { bookerEmail, eventType, eventName } = params;
  const trimmed = bookerEmail.trim();

  let crmLine = `CRM: ${HUB_BASE_URL}/search  →  search "${trimmed}" (no record on file yet)`;
  try {
    const { getServiceSupabaseOrThrow } = await import('@wtc/supabase');
    const supabase = getServiceSupabaseOrThrow();
    // citext is not installed, so match both the raw and lowercased address.
    const candidates = Array.from(new Set([trimmed, trimmed.toLowerCase()]));
    const { data } = await supabase
      .from('contacts')
      .select('id')
      .in('email', candidates)
      .limit(1)
      .maybeSingle();
    if (data?.id) {
      crmLine = `CRM record: ${HUB_BASE_URL}/contacts/${data.id}`;
    }
  } catch (error) {
    console.warn(
      '[PUBLIC_BOOKING] Hub contact lookup for invite footer failed; using CRM search fallback',
      error
    );
  }

  const divider = '──────────────────────────────';
  const lines = [divider, '📋  West Tennessee Consulting — Team Notes'];
  if (eventName) {
    lines.push(`Meeting type: ${eventName}${eventType ? ` (${eventType})` : ''}`);
  }
  lines.push(`Booked by: ${trimmed}`);
  lines.push(crmLine);
  lines.push(divider);
  return lines.join('\n');
}

// ============================================================================
// ACCOUNT STATUS HANDLER
// ============================================================================

/**
 * Get account status for a user
 * Business logic extracted from /api/unifiedtime/status
 */
export async function handleGetAccountStatus(context: RouteContext): Promise<AccountStatus> {
  const { userEmail } = context;

  // Check Google account status
  const googleTokens = await getGoogleTokensFromSupabase(userEmail);
  
  // Get Google email addresses
  let primaryEmail: string | undefined;
  let secondaryEmail: string | undefined;
  
  if (googleTokens?.access_token) {
    primaryEmail = await getGoogleUserEmail(googleTokens.access_token) || undefined;
  }
  
  if (googleTokens?.access_token2) {
    secondaryEmail = await getGoogleUserEmail(googleTokens.access_token2) || undefined;
  }
  
  const googleStatus = {
    primary: !!(googleTokens?.access_token),
    secondary: !!(googleTokens?.access_token2),
    primaryEmail,
    secondaryEmail
  };

  // Check Microsoft account status
  const microsoftTokens = await getMsftTokensFromSupabase(userEmail);
  let microsoftEmail: string | undefined;
  
  if (microsoftTokens?.access_token) {
    microsoftEmail = await getMicrosoftUserEmail(microsoftTokens.access_token) || undefined;
  }
  
  const microsoftStatus = !!(microsoftTokens?.access_token);

  return {
    google: googleStatus,
    microsoft: microsoftStatus,
    microsoftEmail,
    wtcEmailCalendarBridgeConfigured: hasProfessionalEmailCalendarConfig(),
  };
}

// ============================================================================
// OPEN SLOTS HANDLER
// ============================================================================

/**
 * Get available time slots for booking
 * Business logic extracted from /api/unifiedtime/openslots
 */
export async function handleGetOpenSlots(
  context: RouteContext,
  params: OpenSlotsParams
): Promise<any[]> {
  const { userEmail } = context;
  const { emails, startDate, endDate, slotDuration, extendedHours = false } = params;

  // Validate parameters
  validateOpenSlotsParams(params);

  // Log handler start
  logHandlerStart('OPEN_SLOTS', {
    userEmail,
    emailsCount: emails.length,
    startDate,
    endDate,
    slotDuration,
    extendedHours,
  });

  // Create config with custom slot duration if provided
  const config = {
    ...DEFAULT_CONFIG,
    slotDuration: slotDuration || DEFAULT_CONFIG.SLOT_DURATION,
  };

  // Get available calendar days using the CalendarService
  const availableDays = await CalendarService.getAvailableCalendarDays(
    emails,
    startDate,
    endDate,
    config,
    extendedHours
  );

  // Log audit event
  await logOpenSlotsCalculation(context, emails.length, { start: startDate, end: endDate }, config.slotDuration, extendedHours);

  return availableDays;
}

// ============================================================================
// PUBLIC BOOKING HANDLER
// ============================================================================

/**
 * Handle public booking request
 * Business logic extracted from /api/unifiedtime/public-booking
 */
export async function handlePublicBooking(
  context: RouteContext,
  params: PublicBookingParams
): Promise<{
  success: boolean;
  eventId: string;
  message: string;
  /**
   * The assignee the booking was routed to. For an `eventType` (round-robin)
   * booking this is the person `getNextAssignee` selected; for a direct
   * `bookeeEmail` booking it is that email. Exposed so callers (e.g. the Hub
   * IT-support Slack notifier) can attribute the booking without re-running
   * round-robin (which would advance the rotation pointer).
   */
  bookeeEmail?: string;
  meetingDetails?: {
    eventId: string;
    title?: string;
    description?: string;
    location?: string;
    start?: string;
    end?: string;
    timeZone?: string;
    htmlLink?: string;
    hangoutLink?: string;
    isOnline?: boolean;
    attendees?: Array<{ email: string; name?: string }>;
  };
}> {
  const { bookeeEmail: providedBookeeEmail, eventType, email, description = '', location = '', isOnline = false, slot } = params;

  // Validate parameters
  validatePublicBookingParams(params);

  // Log handler start
  logHandlerStart('PUBLIC_BOOKING', {
    email,
    eventType,
    isOnline,
  });

  let bookeeEmail: string;
  let eventName: string | undefined;

  // Determine bookee email (event type or provided)
  if (eventType) {
    const eventConfig = getEventTypeConfig(eventType);
    if (!eventConfig) {
      throw new Error(`Invalid event type: ${eventType}`);
    }

    eventName = eventConfig.name;

    // Perform round robin assignment
    bookeeEmail = await getNextAssignee(eventType, eventConfig.groupEmails);
    logRoundRobinAssignment(eventType, bookeeEmail);
  } else if (providedBookeeEmail) {
    bookeeEmail = providedBookeeEmail;
  } else {
    throw new Error('Either bookeeEmail or eventType must be provided');
  }

  // Internal footer (meeting type + Hub CRM deep link) for the invite body.
  const internalFooter = await buildInternalInviteFooter({
    bookerEmail: email,
    eventType,
    eventName,
  });

  // Build event details with timezone conversion (shared across Google, Microsoft, and email invite)
  const eventDetails = await buildEventDetails(
    email,
    bookeeEmail,
    slot.start,
    slot.end,
    description,
    location,
    isOnline,
    eventName,
    internalFooter
  );

  logTimezoneConversion(
    slot.start,
    slot.end,
    eventDetails.start.dateTime,
    eventDetails.end.dateTime,
    isOnline,
    !!eventDetails.conferenceData
  );

  const googleTokens = await getGoogleTokens(bookeeEmail);
  const microsoftTokens = await getMicrosoftTokens(bookeeEmail);

  let createdEvent: { id: string };
  let meetingDetails: {
    eventId: string;
    title?: string;
    description?: string;
    location?: string;
    start?: string;
    end?: string;
    timeZone?: string;
    htmlLink?: string;
    hangoutLink?: string;
    isOnline?: boolean;
    attendees?: Array<{ email: string; name?: string }>;
  };

  if (validateTokenExists(googleTokens, 'google')) {
    const event = await createGoogleCalendarEvent(googleTokens!.access_token, eventDetails, isOnline);
    createdEvent = { id: event.id };
    meetingDetails = extractMeetingDetails(
      event,
      email,
      bookeeEmail,
      slot.start,
      slot.end,
      location,
      isOnline
    );
  } else if (validateTokenExists(microsoftTokens, 'microsoft')) {
    const event = await createMicrosoftCalendarEventForBooking(
      microsoftTokens!.access_token,
      eventDetails,
      isOnline
    );
    createdEvent = { id: String(event.id) };
    meetingDetails = extractMicrosoftMeetingDetails(
      event,
      email,
      bookeeEmail,
      slot.start,
      slot.end,
      location,
      isOnline
    );
  } else {
    const fallbackUid =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `booking-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    const resolvedOrganizerEmail = (await resolveEmailClientAddressFromAuthEmail(bookeeEmail)) || bookeeEmail;

    let calendarUid = fallbackUid;
    const wtcCreate = await createProfessionalEmailCalendarEventViaHub({
      hostEmail: resolvedOrganizerEmail,
      title: eventDetails.summary,
      startUtc: slot.start,
      endUtc: slot.end,
      description: eventDetails.description,
      location: isOnline ? undefined : location || undefined,
      attendeeEmails: [email, resolvedOrganizerEmail],
      allDay: false,
      timezone: eventDetails.start.timeZone,
      meetLink: undefined,
    });
    if (wtcCreate.ok) {
      calendarUid = wtcCreate.event.eventId;
    } else {
      console.warn(
        `[PUBLIC_BOOKING] WTC Email calendar create skipped (${wtcCreate.reason}); using synthetic UID for ICS`
      );
    }

    const { sendPublicBookingInviteEmails } = await import('./handlers/public-booking-calendar-invite');
    await sendPublicBookingInviteEmails({
      uid: calendarUid,
      title: eventDetails.summary,
      startUtcIso: slot.start,
      endUtcIso: slot.end,
      description: eventDetails.description,
      location: isOnline ? undefined : location || undefined,
      organizerEmail: resolvedOrganizerEmail,
      attendeeEmails: [email, resolvedOrganizerEmail],
      meetLink: undefined,
    });
    createdEvent = { id: calendarUid };
    meetingDetails = {
      eventId: calendarUid,
      title: eventDetails.summary,
      description: eventDetails.description,
      location: isOnline ? 'Online' : location,
      start: slot.start,
      end: slot.end,
      timeZone: eventDetails.start.timeZone,
      isOnline,
      attendees: [{ email }, { email: resolvedOrganizerEmail }],
    };
  }

  // Store booking history if event type provided
  if (eventType) {
    try {
      const { getServiceSupabaseOrThrow } = await import('@wtc/supabase');
      const supabase = getServiceSupabaseOrThrow();

      await supabase.from('booking_history').insert({
        booker_email: email,
        bookee_email: bookeeEmail,
        subject: `Meeting Request from ${email}`,
        start_time: slot.start,
        end_time: slot.end,
        location: isOnline ? '' : location,
        description: description,
        status: 'scheduled',
        event_type: eventType,
      });

      logBookingHistoryStored(eventType);
    } catch (error) {
      logBookingHistoryError(error);
    }
  }

  // Log successful booking
  await logPublicBookingCreated(context, email, bookeeEmail, createdEvent.id, eventType);

  return {
    success: true,
    eventId: createdEvent.id,
    message: 'Event created successfully',
    bookeeEmail,
    meetingDetails,
  };
}

// ============================================================================
// PUBLIC AVAILABILITY HANDLER
// ============================================================================

/**
 * Get public availability for booking
 * Business logic extracted from /api/unifiedtime/public-availability
 */
export async function handleGetPublicAvailability(
  context: RouteContext,
  params: { emails: string[]; startDate: string; endDate: string; slotDuration?: number; availableDurations?: number[] }
): Promise<any[]> {
  const { emails, startDate, endDate, slotDuration, availableDurations } = params;

  // Validate parameters
  validatePublicAvailabilityParams(params);

  // Log handler start
  logHandlerStart('PUBLIC_AVAILABILITY', {
    emailsCount: emails.length,
    startDate,
    endDate,
    slotDuration,
    availableDurations,
  });

  // Create config with custom slot duration if provided
  const config = {
    ...DEFAULT_CONFIG,
    slotDuration: slotDuration || DEFAULT_CONFIG.SLOT_DURATION,
  };

  const { connected: connectedEmails, disconnected: disconnectedEmails } = await checkEmailsTokenStatus(emails);

  const fromUtc = new Date(`${startDate}T00:00:00.000Z`).toISOString();
  const toUtc = new Date(`${endDate}T23:59:59.999Z`).toISOString();

  const connectedSet = new Set(connectedEmails.map((e) => e.toLowerCase()));
  const wtcResolvedEmails = new Set<string>();
  if (hasProfessionalEmailCalendarConfig() && disconnectedEmails.length > 0) {
    for (const e of disconnectedEmails) {
      const resolution = await fetchProfessionalEmailCalendarWithResolution({
        email: e,
        fromUtc,
        toUtc,
      });
      if (resolution.resolved) {
        wtcResolvedEmails.add(e.toLowerCase());
      }
    }
  }

  const emailsForAvailability = emails.filter(
    (e) => connectedSet.has(e.toLowerCase()) || wtcResolvedEmails.has(e.toLowerCase())
  );

  if (emailsForAvailability.length === 0) {
    throw createCalendarNotConnectedError(
      disconnectedEmails.length > 0 ? disconnectedEmails : emails
    );
  }

  if (disconnectedEmails.length > 0) {
    logPartialAvailability(
      connectedEmails.length,
      disconnectedEmails.length,
      connectedEmails,
      disconnectedEmails
    );
  }

  const availableDays = await CalendarService.getAvailableCalendarDays(
    emailsForAvailability,
    startDate,
    endDate,
    config,
    false
  );

  // Process slots (deduplicate and filter by duration)
  const processedDays = processAvailableSlots(availableDays, availableDurations);

  // Log filtering results if durations were applied
  if (availableDurations && availableDurations.length > 0) {
    logSlotFiltering(availableDays.length, processedDays.length, availableDurations);
  }

  return processedDays;
}

// ============================================================================
// ACCOUNT CLEAR HANDLER
// ============================================================================

/**
 * Clear/reset user account
 * Business logic extracted from /api/unifiedtime/clear
 */
export async function handleClearAccount(context: RouteContext): Promise<{ msft_deleted: boolean; google_deleted: boolean; message: string }> {
  const { userEmail } = context;

  logHandlerStart('ACCOUNT_CLEAR', { userEmail });

  // Remove all connections
  const result = await removeAllConnections(userEmail);

  // Log audit event
  await logAccountDataCleared(context, result.microsoft, result.google);

  return {
    msft_deleted: result.microsoft,
    google_deleted: result.google,
    message: 'Account data cleared successfully',
  };
}

// ============================================================================
// REMOVE CONNECTION HANDLER
// ============================================================================

/**
 * Remove a calendar connection
 * Business logic extracted from /api/unifiedtime/remove-connection
 */
export async function handleRemoveConnection(
  context: RouteContext,
  connectionType: 'google' | 'microsoft' | 'google-primary' | 'google-secondary'
): Promise<{ success: boolean; message: string; connectionType: string; details: any }> {
  const { userEmail } = context;

  logHandlerStart('REMOVE_CONNECTION', { userEmail, connectionType });

  // Validate connection type
  validateConnectionType(connectionType);

  try {
    // Remove connection by type
    const result = await removeConnectionByType(userEmail, connectionType);

    // Log audit event
    await logConnectionRemoved(context, connectionType, result.success);

    return result;
  } catch (error) {
    // Log failed audit event
    await logConnectionRemovalFailed(context, connectionType, error instanceof Error ? error : new Error(String(error)));

    throw error;
  }
}

// ============================================================================
// CALENDAR DATA HANDLERS
// ============================================================================

/**
 * Get Google Calendar events
 * Business logic extracted from /api/unifiedtime/google/create
 */
export async function handleGetGoogleCalendarEvents(
  context: RouteContext,
  params: {
    email?: string;
    monthsAgo?: number;
    loadFirstAccountOnly?: boolean;
    loadSecondAccountOnly?: boolean;
    code?: string;
    code_verifier?: string;
    isSecondAccount?: boolean;
  }
): Promise<any[]> {
  const { userEmail } = context;
  
  // Implementation would depend on your specific calendar data fetching logic
  // This is a placeholder for the actual business logic
  
  return [];
}

/**
 * Get Microsoft Calendar events
 * Business logic extracted from /api/unifiedtime/getCalendar/msft
 */
export async function handleGetMicrosoftCalendarEvents(
  context: RouteContext,
  params: {
    email?: string;
    monthsAgo?: number;
    code?: string;
    code_verifier?: string;
    isSecondAccount?: boolean;
  }
): Promise<any[]> {
  const { userEmail } = context;
  
  // Implementation would depend on your specific calendar data fetching logic
  // This is a placeholder for the actual business logic
  
  return [];
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Create a standardized route context from a NextRequest
 */
export async function createRouteContext(
  request: any, // Accept both Request and NextRequest
  extractUserEmailFromToken: (request: any) => Promise<string | null>,
  getClientIP?: (request: any) => string | null,
  getUserAgent?: (request: any) => string | null
): Promise<RouteContext> {
  const userEmail = await extractUserEmailFromToken(request);
  if (!userEmail) {
    throw new Error('No valid authorization token found');
  }

  return {
    userEmail,
    request,
    clientIP: getClientIP?.(request) || undefined,
    userAgent: getUserAgent?.(request) || undefined
  };
}

/**
 * Create a public route context that doesn't require authentication
 * Used for public endpoints like public booking and availability
 */
export async function createPublicRouteContext(
  request: any, // Accept both Request and NextRequest
  getClientIP?: (request: any) => string | null,
  getUserAgent?: (request: any) => string | null
): Promise<RouteContext> {
  return {
    userEmail: 'public', // Use a placeholder for public requests
    request,
    clientIP: getClientIP?.(request) || undefined,
    userAgent: getUserAgent?.(request) || undefined
  };
}

/**
 * Handle external event booking
 * Business logic for creating events in external calendars
 */
export async function handleExternalEventBooking(
  context: RouteContext,
  params: {
    email: string;
    startTime: string;
    endTime: string;
    title: string;
    description?: string;
    calendarType: 'google' | 'microsoft';
  }
): Promise<{ eventId: string; success: boolean; message: string }> {
  const { userEmail } = context;
  const { email, startTime, endTime, title, description, calendarType } = params;

  logHandlerStart('EXTERNAL_EVENT_BOOKING', {
    userEmail,
    calendarType,
    email,
    title,
  });

  try {
    // This is a placeholder implementation
    // The actual calendar integration would be implemented here
    const eventId = `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const success = true;
    const message = `${calendarType} Calendar event created successfully`;

    // Log external event created
    await logExternalEventCreated(context, calendarType, eventId, email, title, startTime, endTime);

    return { eventId, success, message };
  } catch (error) {
    logHandlerError('EXTERNAL_EVENT_BOOKING', error);
    throw error;
  }
}

/**
 * Handle route errors consistently
 */
export function handleRouteError(error: any, context: RouteContext): Response {
  logHandlerError('ROUTE', error);

  const statusCode = error.message?.includes('authorization')
    ? 401
    : error.message?.includes('required')
      ? 400
      : 500;

  return createErrorResponse(error.message || 'Internal server error', statusCode);
}
